import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import designThemeApi from "../../../services/designThemeApi";

const localDateInput = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export default function ActivationModal({
  show,
  onClose,
  activation,
  themes,
  defaultThemeId,
  defaultPriority = 10,
  defaultPlatform = 1,
  defaultPage = 1,
  onSaved,
}) {
  const isEdit = !!activation;
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    id: "",
    theme_id: "",
    platform: defaultPlatform,
    section_for: defaultPage,
    start_at: "",
    end_at: "",
    priority: defaultPriority,
    is_default: 0,
    status: 1,
  });

  useEffect(() => {
    if (activation) {
      setForm({
        id: activation.id,
        theme_id: activation.themeId,
        platform: activation.platform,
        section_for: activation.sectionFor,
        start_at: localDateInput(activation.startAt),
        end_at: localDateInput(activation.endAt),
        priority: activation.priority ?? 10,
        is_default: activation.isDefault ?? 0,
        status: activation.status ?? 1,
      });
    } else {
      setForm((p) => ({
        ...p,
        id: "",
        theme_id: defaultThemeId || themes?.[0]?.id || "",
        platform: defaultPlatform,
        section_for: defaultPage,
        start_at: "",
        end_at: "",
        priority: defaultPriority,
        is_default: 0,
        status: 1,
      }));
    }
  }, [activation, themes, defaultPlatform, defaultPage, defaultThemeId, defaultPriority]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.theme_id) {
      alert("Theme is required");
      return;
    }

    const start = form.start_at ? Date.parse(form.start_at.replace(' ', 'T')) : null;
    const end = form.end_at ? Date.parse(form.end_at.replace(' ', 'T')) : null;
    if ((form.start_at && !Number.isFinite(start)) || (form.end_at && !Number.isFinite(end)) || (start != null && end != null && end <= start)) {
      alert('Enter valid dates. The end must be after the start.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...(isEdit ? { id: form.id } : {}),
        theme_id: parseInt(form.theme_id),
        platform: parseInt(form.platform),
        section_for: parseInt(form.section_for),
        start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
        priority: parseInt(form.priority ?? 10),
        is_default: parseInt(form.is_default || 0),
        status: parseInt(form.status || 1),
      };

      const res = await designThemeApi.saveActivation(payload);
      if (!res?.success) {
        alert(res?.message || "Failed to save activation");
        return;
      }
      onSaved?.();
    } catch (err) {
      console.error(err);
      alert("Failed to save activation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" className="theme-publishing-modal">
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? "Edit publishing schedule" : "Publish or schedule a theme"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="text-muted mb-3">Choose when this theme should appear. Leave the start empty for now, or choose a future date. After saving, check Published now to confirm the result.</p>
          <div className="small text-muted mb-3">Dates use your local time: {Intl.DateTimeFormat().resolvedOptions().timeZone}.</div>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Theme</Form.Label>
                <Form.Select name="theme_id" value={form.theme_id} onChange={onChange}>
                  {(themes || []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.code})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>Platform</Form.Label>
                <Form.Select name="platform" value={form.platform} onChange={onChange}>
                  <option value={1}>WEB</option>
                  <option value={2}>MOBILE</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>Page</Form.Label>
                <Form.Select name="section_for" value={form.section_for} onChange={onChange}>
                  <option value={1}>HOME</option>
                  <option value={2}>OFFERS</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Start date and time (optional)</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="start_at"
                  value={form.start_at}
                  onChange={onChange}
                  placeholder="YYYY-MM-DD HH:mm:ss"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>End date and time (optional)</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="end_at"
                  value={form.end_at}
                  onChange={onChange}
                  placeholder="YYYY-MM-DD HH:mm:ss"
                />
              </Form.Group>
            </Col>

          </Row>

          <details className="mt-3 border-top pt-3"><summary>Advanced publishing options</summary><p className="small text-muted mt-2">When schedules overlap, the lowest priority number wins. Keep a default fallback for each page.</p><Row className="g-3 mt-1">            <Col md={4}>
              <Form.Group>
                <Form.Label>Priority (lower = higher)</Form.Label>
                <Form.Control
                  type="number"
                  name="priority"
                  value={form.priority}
                  onChange={onChange}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Use as fallback</Form.Label>
                <Form.Select name="is_default" value={form.is_default} onChange={onChange}>
                  <option value={0}>No</option>
                  <option value={1}>Yes — when no festival schedule is active</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select name="status" value={form.status} onChange={onChange}>
                  <option value={1}>Active</option>
                  <option value={2}>Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
</Row></details>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save publishing schedule"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

